import React, { useState } from 'react';
import { WorkExperience, Certification } from '../../../types/index';
import { Box, Typography, Chip, Paper, CircularProgress, IconButton, Collapse } from '@mui/material';
import {
  MdWork,
  MdSchool,
  MdVerified,
  MdCalendarToday,
  MdBusiness,
  MdLocationOn,
  MdExpandMore,
  MdExpandLess,
  MdInsertDriveFile,
} from 'react-icons/md';
import { env } from '../../../config';

interface QualificationsTabProps {
  workExperience: WorkExperience[];
  certifications: Certification[];
}

const QualificationsTab: React.FC<QualificationsTabProps> = ({ workExperience, certifications }) => {
  // State for expand/collapse functionality
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  
  // State for caching preview URLs (avoids repeated API calls)
  const [previewUrls, setPreviewUrls] = useState<{ [key: number]: string }>({});
  
  // State for loading previews
  const [loadingPreviews, setLoadingPreviews] = useState<{ [key: number]: boolean }>({});

  // Get file download URL from agents-images API
  const getFileDownloadUrl = async (fileUrl: string): Promise<string | null> => {
    if (!fileUrl) return null;
    
    try {
      const response = await fetch(`${env.IMAGE_ENDPOINT_URL}/agents-images?url=${encodeURIComponent(fileUrl)}`, {
        method: 'GET'
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.downloadUrl || null;
      }
    } catch (error) {
      console.error("Error fetching file download URL:", error);
    }
    return null;
  };

  // Toggle expand/collapse and load preview on demand
  const toggleExpand = async (id: number, fileUrl: string | null) => {
    const newExpandedIds = new Set(expandedIds);
    
    if (expandedIds.has(id)) {
      // Collapse
      newExpandedIds.delete(id);
    } else {
      // Expand
      newExpandedIds.add(id);
      
      // Load preview if file exists and not already cached
      if (fileUrl && !previewUrls[id]) {
        setLoadingPreviews({ ...loadingPreviews, [id]: true });
        const downloadUrl = await getFileDownloadUrl(fileUrl);
        if (downloadUrl) {
          setPreviewUrls({ ...previewUrls, [id]: downloadUrl });
        }
        setLoadingPreviews({ ...loadingPreviews, [id]: false });
      }
    }
    
    setExpandedIds(newExpandedIds);
  };
  const formatDate = (dateString?: string | null, isEnd: boolean = false) => {
    // If this is an end date and no value is provided, treat as "Present"
    if (isEnd && !dateString) return 'Present';

    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  const getFileExtension = (url: string | null): string => {
    if (!url) return '';
    const extension = url.split('.').pop()?.toLowerCase() || '';
    return extension;
  };

  const isImage = (url: string | null): boolean => {
    const ext = getFileExtension(url);
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
  };

  const isPDF = (url: string | null): boolean => {
    const ext = getFileExtension(url);
    return ext === 'pdf';
  };

  // Render file preview with proper sizing (500×300px)
  const renderFilePreview = (certId: number, originalFileUrl: string | null) => {
    const previewUrl = previewUrls[certId];
    const isLoading = loadingPreviews[certId];

    // Show loading state
    if (isLoading) {
      return (
        <Box 
          sx={{ 
            height: '300px', 
            width: '100%',
            maxWidth: '500px',
            margin: '0 auto',
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 2
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading preview...
          </Typography>
        </Box>
      );
    }

    // If no preview URL yet, return null
    if (!previewUrl) return null;

    // Detect file type from original URL
    if (isImage(originalFileUrl)) {
      return (
        <Box 
          sx={{ 
            maxWidth: '500px',
            margin: '0 auto',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: '#fafafa'
          }}
        >
          <Box 
            component="img"
            src={previewUrl}
            alt="Certificate"
            sx={{ 
              maxHeight: '300px', 
              width: '100%',
              objectFit: 'contain',
              cursor: 'pointer',
              display: 'block'
            }}
            onClick={() => window.open(previewUrl, '_blank')}
          />
        </Box>
      );
    }

    if (isPDF(originalFileUrl)) {
      return (
        <Box sx={{ 
          height: '300px', 
          width: '100%',
          maxWidth: '500px',
          margin: '0 auto',
          border: '1px solid #e0e0e0', 
          borderRadius: 2, 
          overflow: 'hidden',
          backgroundColor: '#fafafa'
        }}>
          <embed
            src={previewUrl}
            type="application/pdf"
            width="100%"
            height="100%"
          />
        </Box>
      );
    }

    // For other file types, show download link
    return (
      <Box 
        sx={{ 
          height: '150px', 
          width: '100%',
          maxWidth: '500px',
          margin: '0 auto',
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          border: '1px dashed #ccc',
          borderRadius: 2,
          backgroundColor: '#f5f5f5',
          gap: 2
        }}
      >
        <MdInsertDriveFile style={{ fontSize: 48, color: '#9ca3af' }} />
        <Typography variant="body2" color="text.secondary">
          Preview not available
        </Typography>
        <Chip 
          label="Open in new tab" 
          color="primary" 
          clickable
          onClick={() => window.open(previewUrl, '_blank')}
        />
      </Box>
    );
  };

  return (
    <Box>
      {/* Work Experience Section */}
      {workExperience && workExperience.length > 0 && (
        <Box mb={5}>
          <Box display="flex" alignItems="center" mb={3}>
            <MdWork style={{ marginRight: 8, color: 'var(--mui-palette-primary-main, #1976d2)' }} />
            <Typography variant="h5" fontWeight="bold">
              Work Experience
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {workExperience.map((work) => (
              <Paper 
                key={work.id} 
                elevation={0}
                sx={{ 
                  p: 3, 
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    transition: 'box-shadow 0.3s ease'
                  }
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {work.work_position}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <MdBusiness style={{ color: 'var(--mui-palette-text-secondary, #6b7280)', fontSize: 18 }} />
                      <Typography variant="body1" color="text.secondary">
                        {work.work_company}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <MdLocationOn style={{ color: 'var(--mui-palette-text-secondary, #6b7280)', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {work.work_location}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip 
                    label={work.work_type} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </Box>
                
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <MdCalendarToday style={{ color: 'var(--mui-palette-text-secondary, #6b7280)', fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(work.work_from)} - {formatDate(work.work_to, true)}
                  </Typography>
                </Box>

                {work.work_summary && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {work.work_summary}
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* Certifications Section */}
      {certifications && certifications.length > 0 && (
        <Box>
          <Box display="flex" alignItems="center" mb={3}>
            <MdSchool style={{ marginRight: 8, color: 'var(--mui-palette-primary-main, #1976d2)' }} />
            <Typography variant="h5" fontWeight="bold">
              Certifications
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {certifications.map((cert) => {
              const isExpanded = expandedIds.has(cert.id);
              const fileUrl = cert.file?.imageUrlS3 || null;
              const hasFile = !!fileUrl;

              return (
                <Paper 
                  key={cert.id} 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      transition: 'box-shadow 0.3s ease'
                    }
                  }}
                >
                  {/* Certification Header - Always Visible */}
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="space-between"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => toggleExpand(cert.id, fileUrl)}
                  >
                    <Box display="flex" alignItems="center" gap={1} flex={1}>
                      <Typography variant="h6" fontWeight="600">
                        {cert.certName}
                      </Typography>
                      {cert.verified && (
                        <Chip 
                          icon={<MdVerified />}
                          label="Verified" 
                          size="small" 
                          color="success"
                        />
                      )}
                    </Box>
                    <IconButton size="small">
                      {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                    </IconButton>
                  </Box>

                  {/* Expanded Details */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box mt={2}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Certificate No: <strong>{cert.certificateNo}</strong>
                      </Typography>

                      <Box mb={2} mt={1}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Issuing Body:</strong> {cert.issuingBody}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <MdCalendarToday style={{ color: 'var(--mui-palette-text-secondary, #6b7280)', fontSize: 16 }} />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Issued:</strong> {formatDate(cert.issuedAt)}
                        </Typography>
                      </Box>

                      {cert.expiresAt && (
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <MdCalendarToday style={{ color: 'var(--mui-palette-text-secondary, #6b7280)', fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary">
                            <strong>Expires:</strong> {formatDate(cert.expiresAt)}
                          </Typography>
                        </Box>
                      )}

                      {/* Certificate File Preview - Loaded on-demand */}
                      {hasFile && (
                        <Box mt={3} mb={2}>
                          {renderFilePreview(cert.id, fileUrl)}
                        </Box>
                      )}

                      {cert.verifiedByName && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          <strong>Verified by:</strong> {cert.verifiedByName}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}

      {(!workExperience || workExperience.length === 0) && (!certifications || certifications.length === 0) && (
        <Box textAlign="center" py={5}>
          <Typography variant="body1" color="text.secondary">
            No qualifications information available.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QualificationsTab;
